import React from 'react';
import { Modal, Table } from 'react-bootstrap';
import Badge, { BadgeBg } from "components/base/Badge";
import { useTranslation } from 'react-i18next';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarehouse, faCheck, faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

interface StockInfo {
  qty: number;
  salable_qty?: number;
  is_in_stock: boolean;
}

interface StockDetailsModalProps {
  show: boolean;
  onHide: () => void;
  productName: string;
  productSku: string;
  stock: StockInfo;
}

const StockDetailsModal: React.FC<StockDetailsModalProps> = ({
  show,
  onHide,
  productName,
  productSku,
  stock
}) => {
  const { t } = useTranslation();

  // Calculate difference between total qty and salable qty
  const reservedQty = stock.salable_qty !== undefined ? (stock.qty - stock.salable_qty) : 0;

  return (
    <Modal show={show} onHide={onHide} centered className="stock-details-modal">
      <Modal.Header closeButton className="bg-body-tertiary">
        <Modal.Title className="text-body">
          <FontAwesomeIcon icon={faWarehouse} className="me-2" />
          {t('products.stockDetails', 'Stock Details')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        <div className="mb-4">
          <h5 className="fw-bold">{productName}</h5>
          <p className="text-muted">{t('sku')}: {productSku}</p>
        </div>

        <div className="border rounded p-3 mb-4">
          <Table className="table-borderless mb-0">
            <tbody>
              <tr>
                <td className="ps-0 fw-semibold text-body">{t('products.totalStock', 'Total Stock')}:</td>
                <td className="pe-0 text-end">
                  <Badge 
                    bg={stock.qty > 0 ? 'success' : 'danger'} 
                    variant="phoenix"
                    className="fs-10 px-2 py-1"
                  >
                    {stock.qty}
                  </Badge>
                </td>
              </tr>
              <tr>
                <td className="ps-0 fw-semibold text-body">{t('products.salableStock', 'Salable Stock')}:</td>
                <td className="pe-0 text-end">
                  <Badge 
                    bg={stock.salable_qty && stock.salable_qty > 0 ? 'success' : 'danger'} 
                    variant="phoenix"
                    className="fs-10 px-2 py-1"
                  >
                    {stock.salable_qty !== undefined ? stock.salable_qty.toFixed(1) : t('notAvailable')}
                  </Badge>
                </td>
              </tr>
              {stock.salable_qty !== undefined && (
                <tr>
                  <td className="ps-0 fw-semibold text-body">{t('products.reservedStock', 'Reserved Stock')}:</td>
                  <td className="pe-0 text-end">
                    <Badge 
                      bg={reservedQty > 0 ? 'warning' : 'secondary'} 
                      variant="phoenix"
                      className="fs-10 px-2 py-1"
                    >
                      {reservedQty.toFixed(1)}
                    </Badge>
                  </td>
                </tr>
              )}
              <tr>
                <td className="ps-0 fw-semibold text-body">{t('products.stockStatus', 'Stock Status')}:</td>
                <td className="pe-0 text-end">
                  {stock.is_in_stock ? (
                    <Badge 
                      bg="success" 
                      variant="phoenix"
                      className="fs-10 px-2 py-1 d-flex align-items-center justify-content-center"
                    >
                      <FontAwesomeIcon icon={faCheck} className="me-1" />
                      {t('products.inStock', 'In Stock')}
                    </Badge>
                  ) : (
                    <Badge 
                      bg="danger" 
                      variant="phoenix"
                      className="fs-10 px-2 py-1 d-flex align-items-center justify-content-center"
                    >
                      <FontAwesomeIcon icon={faTimes} className="me-1" />
                      {t('products.outOfStock', 'Out of Stock')}
                    </Badge>
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
        
        <div className="bg-body-tertiary p-3 rounded d-flex align-items-start">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2 mt-1 text-primary" />
          <div>
            <p className="mb-1 fs-9">
              <strong>{t('products.stockInfoTitle', 'Stock Information')}:</strong>
            </p>
            <ul className="ps-3 mb-0 fs-9">
              <li className="mb-1">{t('products.totalStockInfo', 'Total Stock is the physical inventory in the warehouse.')}</li>
              <li className="mb-1">{t('products.salableStockInfo', 'Salable Stock is the quantity available for sale, taking into account pending orders and reservations.')}</li>
              <li>{t('products.reservedStockInfo', 'Reserved Stock represents quantities allocated to pending orders but not yet shipped.')}</li>
            </ul>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="bg-body-tertiary">
        <Button variant="outline-secondary" className="px-4" onClick={onHide}>
          {t('common.close', 'Close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StockDetailsModal;
